package com.havn.app.features.notifications.domain

enum class HavnNotificationType(
    val databaseValue: String,
    val title: String,
    val description: String
) {
    NewLeadCaptured("new_lead_captured", "New lead captured", "When an open house or QR flow captures a lead."),
    VisitorMarkedHotLead("visitor_marked_hot_lead", "Visitor marked as hot lead", "Only when a lead becomes high intent."),
    OpenHouseStartsSoon("open_house_starts_soon", "Open house starts soon", "A single reminder 30 minutes before start."),
    OpenHouseSummaryReady("open_house_summary_ready", "Open house summary ready", "When the post-event summary is ready."),
    SellerReportGenerated("seller_report_generated", "Seller report generated", "When a seller report has been created."),
    AiFollowUpReady("ai_follow_up_ready", "AI follow-up ready", "When a follow-up draft is ready to review."),
    AgentInvitedToAgency("agent_invited_to_agency", "Agent invited to agency", "Agency invitations and membership changes."),
    LeadAssigned("lead_assigned", "Lead assigned to you", "When a teammate assigns a lead to you."),
    LeadTransferred("lead_transferred", "Lead transferred to you", "When ownership of a lead moves to you."),
    SubscriptionIssue("subscription_issue", "Subscription issue", "Renewal failures and billing problems.");

    companion object {
        fun fromDatabase(value: String): HavnNotificationType =
            entries.firstOrNull { it.databaseValue == value } ?: NewLeadCaptured
    }
}

enum class HavnDevicePlatform(val databaseValue: String) {
    Android("android"),
    Ios("ios"),
    Web("web")
}

data class HavnNotificationSettings(
    val quietHoursEnabled: Boolean = false,
    val quietHoursStart: String = "22:00",
    val quietHoursEnd: String = "07:00",
    val timezone: String = "UTC"
)

data class HavnNotificationPreferences(
    val enabledByType: Map<HavnNotificationType, Boolean> =
        HavnNotificationType.entries.associateWith { true },
    val settings: HavnNotificationSettings = HavnNotificationSettings()
) {
    fun enabled(type: HavnNotificationType): Boolean = enabledByType[type] ?: true
}

data class HavnDeviceToken(
    val token: String,
    val platform: HavnDevicePlatform,
    val deviceName: String? = null,
    val appVersion: String? = null
)

interface NotificationRepository {
    suspend fun preferences(userId: String): Result<HavnNotificationPreferences>
    suspend fun registerDeviceToken(token: HavnDeviceToken): Result<Unit>
    suspend fun setPreference(type: HavnNotificationType, enabled: Boolean): Result<Unit>
    suspend fun setQuietHours(settings: HavnNotificationSettings): Result<Unit>
}
